import { IColumnSettings } from '../../../entities/IClusters';
import { EColumnType, normalizeGridColumnType } from '../../../entities/IDatagrid';

type ColumnSizeValue = number | string;

export interface IColumnSizing {
    width: ColumnSizeValue;
    maxWidth: ColumnSizeValue;
    minWidth: ColumnSizeValue;
}

export const getColumnSizing = (column: IColumnSettings): IColumnSizing => {
    let width: ColumnSizeValue = '100%';
    let maxWidth: ColumnSizeValue = 'auto';
    let minWidth: ColumnSizeValue = 'auto';

    const baseType = normalizeGridColumnType(column.type) ?? column.type;
    const finalType = `${baseType ?? ''}${column.subType || ''}` as EColumnType;

    switch (finalType) {
        case EColumnType.date:
        case EColumnType.dateAgo:
            maxWidth = 112;
            minWidth = 112;
            break;
        case EColumnType.number:
            minWidth = 56;
            width = 56;
            break;
        case EColumnType.exclamationMark:
            maxWidth = 176;
            break;
        case EColumnType.NUMBER_HASH:
        case EColumnType.NUMBER_ORDER:
            maxWidth = 72;
            break;
        case EColumnType.extendedAvatar:
            minWidth = 232;
            break;
        case EColumnType.hyperlink:
        case EColumnType.HYPERLINK_MESSAGES:
        case EColumnType.HYPERLINK_DOCUMENTS:
        case EColumnType.HYPERLINK_APPROVAL:
            minWidth = 112;
            break;
        case EColumnType.overview:
        case EColumnType.OVERVIEW_CLUSTER:
        case EColumnType.OVERVIEW_ORGANIZATION:
        case EColumnType.richText:
        case EColumnType.richTextFlattened:
        case EColumnType.text:
            minWidth = 384;
            maxWidth = 384;
            break;
        case EColumnType.ACTIVITYMESSAGE:
            minWidth = 480;
            break;
        case EColumnType.people:
            minWidth = 192;
            maxWidth = 192;
            break;
        case EColumnType.enum:
            width = 92;
            minWidth = 92;
            break;
        case EColumnType.ENUM_STATUS:
        case EColumnType.STATUS_DATE:
        case EColumnType.STATUS_COLOR:
        case EColumnType.status:
        case EColumnType.party:
        case EColumnType.parties:
        case EColumnType.person:
        case EColumnType.principal:
        case EColumnType.multiplePrincipals:
        case EColumnType.PROGRESS:
            minWidth = 192;
            break;
        case EColumnType.boolean:
            minWidth = 56;
            break;
        case EColumnType.clusterTemplate:
        case EColumnType.image:
        case EColumnType.ICON:
            minWidth = 72;
            break;
        case EColumnType.pill:
        case EColumnType.referentials:
        case EColumnType.referential:
            minWidth = 176;
            break;
        case EColumnType.dropdown:
            minWidth = 232;
            break;
        case EColumnType.universalSwitcher:
            minWidth = 'fit-content';
            break;
        case EColumnType.button:
            minWidth = 'fit-content';
            break;
        case EColumnType.lastEditedBy:
        case EColumnType.LAST_EDITED_BY_RELATIVE_DATE:
            minWidth = 200;
            break;
        default:
            width = 'auto';
            maxWidth = 'auto';
            minWidth = 'auto';
            break;
    }

    if (column.width) {
        width = column.width;
    }
    if (column.minWidth) {
        minWidth = column.minWidth;
    }
    if (column.maxWidth) {
        maxWidth = column.maxWidth;
    }

    if (column.customWidth) {
        width = column.customWidth;
        if (!column.minWidth) {
            minWidth = column.customWidth;
        }
        if (!column.maxWidth) {
            maxWidth = column.customWidth;
        }
    }

    return {
        width,
        maxWidth,
        minWidth
    };
};

export type IColumnWidths = Record<string, number>;

export const MIN_RESIZED_COLUMN_WIDTH = 48;
export const FIXED_LAYOUT_ATTRIBUTE = 'data-fixed-layout';
const DEFAULT_RESIZED_COLUMN_WIDTH = 160;

export const hasColumnWidths = (widths?: IColumnWidths): boolean => !!widths && Object.keys(widths).length > 0;

const getFallbackColumnWidth = (column: IColumnSettings): number => {
    const { width, minWidth } = getColumnSizing(column);
    if (typeof width === 'number') {
        return Math.max(width, typeof minWidth === 'number' ? minWidth : 0);
    }
    return typeof minWidth === 'number' ? minWidth : DEFAULT_RESIZED_COLUMN_WIDTH;
};

export const applyColumnWidths = (columns: IColumnSettings[], widths?: IColumnWidths): IColumnSettings[] => {
    if (!hasColumnWidths(widths)) {
        return columns;
    }
    return columns.map(column => {
        const width = widths[column.field] ?? getFallbackColumnWidth(column);
        return { ...column, customWidth: width, minWidth: width, maxWidth: width };
    });
};

export const getColumnsTotalWidth = (columns: IColumnSettings[]): number =>
    columns.reduce((sum, column) => sum + (typeof column.customWidth === 'number' ? column.customWidth : 0), 0);

export const normalizeSavedColumnWidths = (raw: unknown): IColumnWidths | undefined => {
    let value = raw;
    if (typeof value === 'string') {
        try {
            value = JSON.parse(value);
        } catch {
            return undefined;
        }
    }
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return undefined;
    }
    const widths: IColumnWidths = {};
    Object.entries(value as Record<string, unknown>).forEach(([field, width]) => {
        const numeric = Number(width);
        if (Number.isFinite(numeric) && numeric > 0) {
            widths[field] = Math.max(MIN_RESIZED_COLUMN_WIDTH, Math.round(numeric));
        }
    });
    return hasColumnWidths(widths) ? widths : undefined;
};
